import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketVolumeChart = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetrics, setSelectedMetrics] = useState(['tickets', 'resolved', 'response']);

  const chartData = [
    { date: '2025-01-19', tickets: 45, resolved: 38, response: 2.4 },
    { date: '2025-01-20', tickets: 52, resolved: 41, response: 2.8 },
    { date: '2025-01-21', tickets: 38, resolved: 35, response: 2.1 },
    { date: '2025-01-22', tickets: 61, resolved: 48, response: 3.2 },
    { date: '2025-01-23', tickets: 43, resolved: 40, response: 2.6 },
    { date: '2025-01-24', tickets: 55, resolved: 52, response: 2.3 },
    { date: '2025-01-25', tickets: 49, resolved: 46, response: 2.7 },
    { date: '2025-01-26', tickets: 58, resolved: 51, response: 2.9 }
  ];

  const metrics = [
    { key: 'tickets', label: 'New Tickets', color: '#3b82f6', icon: 'Ticket' },
    { key: 'resolved', label: 'Resolved', color: '#10b981', icon: 'CheckCircle' },
    { key: 'response', label: 'Avg Response (hrs)', color: '#f59e0b', icon: 'Clock' }
  ];

  const timeRanges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' }
  ];

  const toggleMetric = (metricKey) => {
    setSelectedMetrics(prev => 
      prev.includes(metricKey) 
        ? prev.filter(m => m !== metricKey)
        : [...prev, metricKey]
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-3">
          <p className="text-sm font-medium text-text-primary mb-2">
            {new Date(label).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            })}
          </p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-text-secondary">{entry.name}:</span>
              <span className="font-medium text-text-primary">
                {entry.dataKey === 'response' ? `${entry.value}h` : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Validate and sanitize chart data
  const validatedChartData = chartData?.filter(item => 
    item && 
    typeof item.date === 'string' && 
    typeof item.tickets === 'number' && 
    typeof item.resolved === 'number' && 
    typeof item.response === 'number'
  ) || [];

  if (!validatedChartData.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-text-secondary mb-4 mx-auto" />
            <p className="text-text-secondary">No data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Support Metrics Trends</h3>
            <p className="text-sm text-text-secondary">Ticket volume and resolution performance</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {timeRanges.map((range) => (
            <Button
              key={range.value}
              variant={timeRange === range.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setTimeRange(range.value)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        {metrics.map((metric) => (
          <button
            key={metric.key}
            onClick={() => toggleMetric(metric.key)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all duration-150 ${
              selectedMetrics.includes(metric.key)
                ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-text-secondary hover:text-text-primary hover:bg-muted/50'
            }`}
          >
            <Icon name={metric.icon} size={16} />
            <span className="text-sm font-medium">{metric.label}</span>
          </button>
        ))}
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={validatedChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="date" 
              stroke="var(--color-text-secondary)"
              fontSize={12}
              tickFormatter={(value) => {
                try {
                  return new Date(value).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  });
                } catch (error) {
                  return value;
                }
              }}
            />
            <YAxis 
              yAxisId="left"
              stroke="var(--color-text-secondary)" 
              fontSize={12} 
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="var(--color-text-secondary)" 
              fontSize={12}
              label={{ value: 'Hours', angle: 90, position: 'insideRight' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {selectedMetrics.includes('tickets') && (
              <Line
                type="monotone"
                dataKey="tickets"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                name="New Tickets"
                yAxisId="left"
                connectNulls={false}
              />
            )}
            
            {selectedMetrics.includes('resolved') && (
              <Line
                type="monotone"
                dataKey="resolved"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                name="Resolved"
                yAxisId="left"
                connectNulls={false}
              />
            )}
            
            {selectedMetrics.includes('response') && (
              <Line
                type="monotone"
                dataKey="response"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                name="Avg Response (hrs)"
                yAxisId="right"
                connectNulls={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TicketVolumeChart;